/**
 * Brazilian Field Masks and Utilities
 * Provides formatting and validation for Brazilian documents and fields
 */

/**
 * Format CPF (000.000.000-00)
 */
export const formatCPF = (value: string): string => {
  const cleaned = value.replace(/\D/g, '');
  const limited = cleaned.substring(0, 11);

  if (limited.length <= 3) return limited;
  if (limited.length <= 6) return `${limited.slice(0, 3)}.${limited.slice(3)}`;
  if (limited.length <= 9) return `${limited.slice(0, 3)}.${limited.slice(3, 6)}.${limited.slice(6)}`;
  return `${limited.slice(0, 3)}.${limited.slice(3, 6)}.${limited.slice(6, 9)}-${limited.slice(9)}`;
};

/**
 * Format CNPJ (00.000.000/0000-00)
 */
export const formatCNPJ = (value: string): string => {
  const cleaned = value.replace(/\D/g, '');
  const limited = cleaned.substring(0, 14);

  if (limited.length <= 2) return limited;
  if (limited.length <= 5) return `${limited.slice(0, 2)}.${limited.slice(2)}`;
  if (limited.length <= 8) return `${limited.slice(0, 2)}.${limited.slice(2, 5)}.${limited.slice(5)}`;
  if (limited.length <= 12) return `${limited.slice(0, 2)}.${limited.slice(2, 5)}.${limited.slice(5, 8)}/${limited.slice(8)}`;
  return `${limited.slice(0, 2)}.${limited.slice(2, 5)}.${limited.slice(5, 8)}/${limited.slice(8, 12)}-${limited.slice(12)}`;
};

/**
 * Format CEP (00000-000)
 */
export const formatCEP = (value: string): string => {
  const cleaned = value.replace(/\D/g, '');
  const limited = cleaned.substring(0, 8);

  if (limited.length <= 5) return limited;
  return `${limited.slice(0, 5)}-${limited.slice(5)}`;
};

/**
 * Format RG (00.000.000-0)
 * RG format varies by state, but we'll use the most common São Paulo format
 */
export const formatRG = (value: string): string => {
  const cleaned = value.replace(/\D/g, '');
  const limited = cleaned.substring(0, 9);

  if (limited.length <= 2) return limited;
  if (limited.length <= 5) return `${limited.slice(0, 2)}.${limited.slice(2)}`;
  if (limited.length <= 8) return `${limited.slice(0, 2)}.${limited.slice(2, 5)}.${limited.slice(5)}`;
  return `${limited.slice(0, 2)}.${limited.slice(2, 5)}.${limited.slice(5, 8)}-${limited.slice(8)}`;
};

/**
 * Format Phone (Brazilian format)
 * (00) 0000-0000 or (00) 00000-0000
 */
export const formatPhone = (value: string): string => {
  const cleaned = value.replace(/\D/g, '');
  const limited = cleaned.substring(0, 11);

  if (limited.length <= 2) return limited;
  if (limited.length <= 6) return `(${limited.slice(0, 2)}) ${limited.slice(2)}`;
  if (limited.length <= 10) {
    // Landline: (00) 0000-0000
    return `(${limited.slice(0, 2)}) ${limited.slice(2, 6)}-${limited.slice(6)}`;
  }
  // Mobile: (00) 00000-0000
  return `(${limited.slice(0, 2)}) ${limited.slice(2, 7)}-${limited.slice(7)}`;
};

/**
 * Remove all formatting from a string (keep only digits)
 */
export const cleanDigits = (value: string): string => {
  return value.replace(/\D/g, '');
};

/**
 * Validate CPF algorithm
 */
export const isValidCPF = (cpf: string): boolean => {
  const cleaned = cleanDigits(cpf);

  if (cleaned.length !== 11) return false;

  // Check for known invalid patterns
  if (/^(\d)\1{10}$/.test(cleaned)) return false;

  // Validate first digit
  let sum = 0;
  for (let i = 0; i < 9; i++) {
    sum += parseInt(cleaned.charAt(i)) * (10 - i);
  }
  let digit1 = 11 - (sum % 11);
  if (digit1 >= 10) digit1 = 0;

  if (digit1 !== parseInt(cleaned.charAt(9))) return false;

  // Validate second digit
  sum = 0;
  for (let i = 0; i < 10; i++) {
    sum += parseInt(cleaned.charAt(i)) * (11 - i);
  }
  let digit2 = 11 - (sum % 11);
  if (digit2 >= 10) digit2 = 0;

  return digit2 === parseInt(cleaned.charAt(10));
};

/**
 * Validate CNPJ algorithm
 */
export const isValidCNPJ = (cnpj: string): boolean => {
  const cleaned = cleanDigits(cnpj);

  if (cleaned.length !== 14) return false;

  // Check for known invalid patterns
  if (/^(\d)\1{13}$/.test(cleaned)) return false;

  // Validate first digit
  const weights1 = [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
  let sum = 0;
  for (let i = 0; i < 12; i++) {
    sum += parseInt(cleaned.charAt(i)) * weights1[i];
  }
  let digit1 = sum % 11;
  digit1 = digit1 < 2 ? 0 : 11 - digit1;

  if (digit1 !== parseInt(cleaned.charAt(12))) return false;

  // Validate second digit
  const weights2 = [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
  sum = 0;
  for (let i = 0; i < 13; i++) {
    sum += parseInt(cleaned.charAt(i)) * weights2[i];
  }
  let digit2 = sum % 11;
  digit2 = digit2 < 2 ? 0 : 11 - digit2;

  return digit2 === parseInt(cleaned.charAt(13));
};

/**
 * Validate CEP format
 */
export const isValidCEP = (cep: string): boolean => {
  const cleaned = cleanDigits(cep);
  return cleaned.length === 8;
};

/**
 * Validate Brazilian phone format
 */
export const isValidPhone = (phone: string): boolean => {
  const cleaned = cleanDigits(phone);
  return cleaned.length === 10 || cleaned.length === 11;
};

/**
 * Tax Regime options for select fields
 * Ordered from smallest to largest company size
 */
export const TAX_REGIMES = [
  { value: 1, label: 'Simples Nacional' },
  { value: 2, label: 'Lucro Presumido' },
  { value: 3, label: 'Lucro Real' },
];

/**
 * Company Type options for select fields
 * Different legal structures for businesses in Brazil
 */
export const COMPANY_TYPES = [
  { value: 1, label: 'Unipessoal LTDA' },
  { value: 2, label: 'Sociedade LTDA' },
  { value: 3, label: 'Individual' },
  { value: 4, label: 'Eireli' },
  { value: 5, label: 'MEI' },
];

/**
 * Employee Range options for select fields
 */
export const EMPLOYEE_RANGES = [
  { value: 1, label: '1 a 10 funcionários' },
  { value: 2, label: '10 a 50 funcionários' },
  { value: 3, label: '50 a 100 funcionários' },
  { value: 4, label: '100 a 300 funcionários' },
  { value: 5, label: '300 a 600 funcionários' },
  { value: 6, label: '600 a 1000 funcionários' },
  { value: 7, label: '1000 a 2000 funcionários' },
  { value: 8, label: '2000 a 4000 funcionários' },
  { value: 9, label: '4000 a 6000 funcionários' },
  { value: 10, label: 'Mais de 6000 funcionários' },
];

/**
 * Brazilian states (UF)
 */
export const BRAZILIAN_STATES = [
  'AC', 'AL', 'AP', 'AM', 'BA', 'CE', 'DF', 'ES', 'GO', 'MA',
  'MT', 'MS', 'MG', 'PA', 'PB', 'PR', 'PE', 'PI', 'RJ', 'RN',
  'RS', 'RO', 'RR', 'SC', 'SP', 'SE', 'TO'
];
